import instructorRouteCatalog from "../../route-specs.json";
import { writeStorage } from "../services/versionedStorage";
import { downloadBrowserFile, openExternalUrl } from "../utils/browser";

/** Complete v3 route catalogue and realistic in-browser fixtures used by the Instructor Platform. */

const INSTRUCTOR_ROLES: Record<TeachingRoleId, TeachingRoleSpec> = {
  "instructor-lead": {
    id: "instructor-lead",
    name: "Instructor Lead",
    shortName: "Lead Instructor",
    description:
      "Leads teaching delivery, instructor assignments, publishing, moderation and final academic escalation.",
    authorities: ["view", "facilitate", "teach", "assess", "approve", "lead"],
    scope: "Assigned programs, cohorts, teaching teams, moderation and delegated final approvals",
  },
  "instructor-assessor": {
    id: "instructor-assessor",
    name: "Instructor & Assessor",
    shortName: "Instructor & Assessor",
    description: "Teaches assigned cohorts and conducts human assessment, grading and evidence review.",
    authorities: ["view", "facilitate", "teach", "assess"],
    scope:
      "Assigned cohorts, assessments, Labs evidence and project reviews; no final moderation or access administration",
  },
  instructor: {
    id: "instructor",
    name: "Instructor",
    shortName: "Instructor",
    description: "Plans lessons, hosts Classes, facilitates Build Rooms and supports assigned learners.",
    authorities: ["view", "facilitate", "teach"],
    scope: "Assigned programs, cohorts, Classes, Build Rooms and teaching content",
  },
  mentor: {
    id: "mentor",
    name: "Mentor",
    shortName: "Mentor",
    description:
      "Supports learners, facilitates discussions and gives formative feedback without grading or final decisions.",
    authorities: ["view", "facilitate"],
    scope: "Explicitly assigned learners, office hours, Build Rooms and project support",
  },
};

export const nowTime = () =>
  new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(new Date());

export const downloadTextFile = downloadBrowserFile;

export function downloadClassCalendar(session: ClassSession) {
  const start = session.id.includes("11-04")
    ? "20261104T120000Z"
    : session.id.includes("11-06")
      ? "20261106T110000Z"
      : "20261110T133000Z";
  const end = session.id.includes("11-04")
    ? "20261104T131500Z"
    : session.id.includes("11-06")
      ? "20261106T120000Z"
      : "20261110T141500Z";
  const content = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Chatoner//AI Academy//EN",
    "BEGIN:VEVENT",
    `UID:${session.id}@academy.chatoner.com`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${session.title}`,
    `DESCRIPTION:${session.program}\nClass code: ${session.code}\nOpen: ${CLASS_JOIN_BASE}`,
    `LOCATION:${CLASS_JOIN_BASE}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  downloadTextFile(`${session.id}.ics`, content, "text/calendar");
}

export function safeLocalStorageSet(key: string, value: string) {
  writeStorage(key, value);
}

export type InstructorArea =
  | "command"
  | "cohorts"
  | "teaching"
  | "class"
  | "buildRooms"
  | "labs"
  | "assessment"
  | "projects"
  | "engagement"
  | "analytics"
  | "search"
  | "notifications"
  | "settings";

export type InstructorModal =
  | null
  | { type: "members" }
  | { type: "language" }
  | { type: "assignment-brief" }
  | { type: "huddle" }
  | { type: "new-channel" }
  | { type: "manage-groups"; payload?: string }
  | { type: "create-class" }
  | { type: "class-invite"; payload?: string }
  | { type: "sync-attendance"; payload?: string }
  | { type: "new-cohort" }
  | { type: "announcement" }
  | { type: "create-lesson" }
  | { type: "create-assignment" }
  | { type: "review-decision"; payload?: string }
  | { type: "assign-lab"; payload?: string }
  | { type: "office-hours" }
  | { type: "export"; payload?: string }
  | { type: "command" }
  | { type: "profile" }
  | { type: "role-switch" }
  | { type: "confirmation"; payload?: string };

export type InstructorNotice = {
  id: number;
  title: string;
  detail: string;
  tone?: "green" | "orange" | "red" | "blue";
};

export type InstructorAuthority = "view" | "facilitate" | "teach" | "assess" | "approve" | "lead";

export type TeachingRoleId = "instructor-lead" | "instructor-assessor" | "instructor" | "mentor";

export type TeachingRoleSpec = {
  id: TeachingRoleId;
  name: string;
  shortName: string;
  description: string;
  authorities: InstructorAuthority[];
  scope: string;
};

export type InstructorRouteSpec = {
  id: string;
  area: InstructorArea;
  page: string;
  title: string;
  purpose: string;
  workflow: string;
  model: string;
  layout: string;
  authority: InstructorAuthority;
  backendDomain: string;
  primaryAction: string;
  signature: string;
  icon: string;
};
export const INSTRUCTOR_ROUTE_SPECS = instructorRouteCatalog as InstructorRouteSpec[];

export const INSTRUCTOR_ROUTE_SPEC_BY_ID = Object.fromEntries(
  INSTRUCTOR_ROUTE_SPECS.map(spec => [spec.id, spec]),
) as Record<string, InstructorRouteSpec>;

export function createDynamicInstructorRouteSpec(area: InstructorArea, page: string): InstructorRouteSpec {
  const title = page
    .replace(/^subgroup-/, "")
    .replace(/^session-/, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, character => character.toUpperCase());
  const authority: InstructorAuthority =
    area === "buildRooms" ? "facilitate" : area === "class" ? "teach" : "view";
  const layout =
    area === "buildRooms" && page.startsWith("subgroup-")
      ? "smaller-group-room"
      : area === "buildRooms"
        ? "custom-collaboration-channel"
        : "dynamic-class-session";
  return {
    id: `${area}/${page}`,
    area,
    page,
    title,
    purpose: `${title}. Dynamically created inside the Instructor Platform with explicit membership, authority, retention and backend scope.`,
    workflow:
      area === "class"
        ? "Schedule → Prepare → Host → Capture → Reconcile"
        : "Define purpose → Assign members → Facilitate → Review → Retain",
    model: `${area}.${page}.${layout}`,
    layout,
    authority,
    backendDomain: area === "class" ? "Chatoner Class Orchestration" : "Build Rooms & Collaboration",
    primaryAction: area === "class" ? "Open Class workflow" : "Facilitate collaboration",
    signature: `INS3-DYN-${Math.abs(
      Array.from(`${area}/${page}`).reduce(
        (total, character) => (total << 5) - total + character.charCodeAt(0),
        0,
      ),
    )
      .toString(16)
      .toUpperCase()}`,
    icon: area === "class" ? "video" : "message",
  };
}

export function resolveStaticInstructorRouteSpec(area: InstructorArea, page: string) {
  return INSTRUCTOR_ROUTE_SPEC_BY_ID[`${area}/${page}`] || null;
}

export function roleCanAccessInstructorRoute(roleId: TeachingRoleId, spec: InstructorRouteSpec) {
  return INSTRUCTOR_ROLES[roleId].authorities.includes(spec.authority);
}

export type LanguageSettings = {
  primary: string;
  secondary: string;
  showTranslation: boolean;
  retainOriginal: boolean;
};

export type ClassSession = {
  id: string;
  title: string;
  code: string;
  dateLabel: string;
  timeLabel: string;
  localTime: string;
  duration: string;
  instructor: string;
  program: string;
  cohort: string;
  mode: string;
  status: "live" | "upcoming" | "completed" | "draft";
  preparation: string[];
  agenda: string[];
  learnerCount: number;
  questions: number;
  recording: "ready" | "processing" | "off" | "scheduled";
  language?: string;
};

export type Cohort = {
  id: string;
  title: string;
  program: string;
  intake: string;
  status: "active" | "upcoming" | "completed";
  learners: number;
  capacity: number;
  progress: number;
  attendance: number;
  onTrack: number;
  watch: number;
  intervention: number;
  reviewQueue: number;
  nextSession: string;
  completionForecast: number;
};

export type LearnerRecord = {
  id: string;
  initials: string;
  name: string;
  email: string;
  cohortId: string;
  cohort: string;
  program: string;
  progress: number;
  attendance: number;
  average: number;
  risk: "on-track" | "watch" | "intervention";
  submissions: string;
  project: string;
  credentialReadiness: number;
  lastActive: string;
  accommodations: string;
  languages: string;
};

export type LessonRecord = {
  id: string;
  title: string;
  module: string;
  program: string;
  format: "Live" | "Self-paced" | "Workshop" | "Clinic";
  status: "Published" | "Draft" | "Review" | "Archived";
  duration: string;
  accessibility: number;
  engagement: number;
  version: string;
};

export type AssignmentRecord = {
  id: string;
  title: string;
  program: string;
  module: string;
  cohorts: string[];
  status: "Published" | "Draft" | "Closed";
  due: string;
  submissions: number;
  graded: number;
  revisions: number;
  rubric: string;
  weight: number;
};

export type SubmissionRecord = {
  id: string;
  learnerId: string;
  learner: string;
  initials: string;
  assignment: string;
  cohort: string;
  submitted: string;
  age: string;
  status: "Pending" | "In review" | "Revision" | "Moderation" | "Graded";
  risk: "low" | "medium" | "high";
  evidence: number;
  score?: number;
  aiDisclosure: boolean;
  integritySignal: string;
};

export type LabRecord = {
  id: string;
  slug: string;
  title: string;
  category: string;
  icon: string;
  entitlement: string;
  access: "Included" | "Advanced";
  assignedCohorts: number;
  activeRuns: number;
  completion: number;
  description: string;
};

export type ProjectReview = {
  id: string;
  learner: string;
  initials: string;
  title: string;
  type: "Practice project" | "Team project" | "Client simulation";
  milestone: string;
  status: "Pending" | "In review" | "Revision" | "Verified";
  architecture: number;
  evidence: number;
  verification: number;
  risk: "low" | "medium" | "high";
};

export type CapstoneReview = {
  id: string;
  learner: string;
  initials: string;
  title: string;
  program: string;
  status: "Panel review" | "Revision" | "Recommended" | "Declined";
  evidenceComplete: number;
  defenseDate: string;
  responsibleUse: "Pass" | "Review" | "Hold";
  recommendation: "Pending" | "Recommend" | "Hold" | "Decline";
};

export type MemberDirectoryItem = {
  id: string;
  initials: string;
  name: string;
  role: "Instructor" | "Mentor" | "Learner" | "Reviewer" | "Observer" | "AI agent";
  detail: string;
  group: "Core team" | "Cohort" | "Academy staff" | "Approved AI";
  online: boolean;
};

export type BuildRoomSubgroup = {
  id: string;
  name: string;
  channel: string;
  purpose: string;
  parentRoomId: string;
  cohortId: string;
  classSessionId: string;
  memberIds: string[];
  leadId: string;
  status: "Active" | "Needs review" | "Complete" | "Paused";
  visibility: "Members + instructors" | "Whole class" | "Instructor only";
  formation: "Manual" | "Balanced" | "Random" | "Language" | "Project role" | "Availability";
  language: string;
  activeNow: number;
  progress: number;
  evidence: number;
  reviewCount: number;
  branch: string;
  labId: string;
  huddleCode: string;
  due: string;
};

export type LabInstructorSectionId =
  | "dashboard"
  | "one-to-one"
  | "content"
  | "authoring"
  | "grading"
  | "learners"
  | "feedback"
  | "reports"
  | "help";

export type LabInstructorWorkspaceSection = {
  id: LabInstructorSectionId;
  label: string;
  icon: string;
  detail: string;
  badge?: string;
};

export type NotificationRecord = {
  id: string;
  icon: string;
  title: string;
  detail: string;
  time: string;
  category: "review" | "class" | "learner" | "cohort" | "system";
  unread: boolean;
  area: InstructorArea;
  page: string;
  deepLink?: string;
};

export type SearchRecord = {
  id: string;
  area: InstructorArea;
  page: string;
  icon: string;
  title: string;
  detail: string;
  label: string;
};

export type OfficeSlot = {
  id: string;
  day: string;
  start: string;
  end: string;
  timezone: string;
  type: string;
  capacity: number;
  bookings: number;
  enabled: boolean;
};

export type MessageThread = {
  id: string;
  subject: string;
  participant: string;
  initials: string;
  role: string;
  channel: "learner" | "cohort" | "staff";
  unread: number;
  preview: string;
  messages: Array<{
    id: string;
    author: string;
    initials: string;
    text: string;
    time: string;
    mine?: boolean;
  }>;
};

export const CLASS_HOST_BASE = "https://class.chatoner.com/host";

export const CLASS_JOIN_BASE = "https://class.chatoner.com/join";

export const LABS_BASE = "https://labs.chatoner.com";

export const ACADEMY_RETURN = "https://instructors.academy.chatoner.com/#/instructor";

export const CLASS_SESSIONS: ClassSession[] = [
  {
    id: "accessible-product-decisions-2026-11-04",
    title: "Accessible Product Decisions",
    code: "4821-771-204",
    dateLabel: "Today · 4 November 2026",
    timeLabel: "3:00 PM EAT",
    localTime: "3:00 PM Kampala time",
    duration: "75 minutes",
    instructor: "Amina Mutesi",
    program: "AI Product Builder Program",
    cohort: "October 2026 Cohort",
    mode: "Live lesson",
    status: "live",
    preparation: [
      "Review Module 6 notes",
      "Open the Kijani acceptance criteria",
      "Test camera, microphone, captions and interpretation",
    ],
    agenda: [
      "Why accessibility is a product decision",
      "Human authority in AI-assisted work",
      "Kijani case review",
      "Reflection and evidence capture",
    ],
    learnerCount: 34,
    questions: 9,
    recording: "scheduled",
  },
  {
    id: "evaluation-clinic-2026-11-06",
    title: "Evaluation Clinic: From Claims to Evidence",
    code: "6114-229-508",
    dateLabel: "Friday · 6 November 2026",
    timeLabel: "2:00 PM EAT",
    localTime: "2:00 PM Kampala time",
    duration: "60 minutes",
    instructor: "Amina Mutesi",
    program: "AI Product Builder Program",
    cohort: "October 2026 Cohort",
    mode: "Cohort clinic",
    status: "upcoming",
    preparation: [
      "Open learner evaluation submissions",
      "Review unresolved risks",
      "Prepare the calibration example",
    ],
    agenda: ["Evaluation design", "Failure analysis", "Evidence quality", "Next review actions"],
    learnerCount: 34,
    questions: 5,
    recording: "scheduled",
  },
  {
    id: "kijani-project-review-2026-11-10",
    title: "Kijani Project Review",
    code: "7708-104-923",
    dateLabel: "Tuesday · 10 November 2026",
    timeLabel: "4:30 PM EAT",
    localTime: "4:30 PM Kampala time",
    duration: "45 minutes",
    instructor: "Amina Mutesi",
    program: "AI Product Builder Program",
    cohort: "Kijani Build Team",
    mode: "Project review",
    status: "upcoming",
    preparation: ["Review staging verification", "Confirm learner merge note", "Inspect credential evidence"],
    agenda: ["Requirement review", "Staging evidence", "Human decision", "Credential handoff"],
    learnerCount: 7,
    questions: 2,
    recording: "off",
  },
  {
    id: "context-architecture-recording-2026-10-28",
    title: "Context Architecture Clinic",
    code: "3982-450-116",
    dateLabel: "Wednesday · 28 October 2026",
    timeLabel: "Completed",
    localTime: "Completed",
    duration: "68 minutes",
    instructor: "Amina Mutesi",
    program: "AI Product Builder Program",
    cohort: "October 2026 Cohort",
    mode: "Clinic",
    status: "completed",
    preparation: [],
    agenda: ["Retrieval boundaries", "Context freshness", "Failure evidence"],
    learnerCount: 32,
    questions: 14,
    recording: "ready",
  },
];

export const COHORTS: Cohort[] = [
  {
    id: "builder-oct-2026",
    title: "AI Product Builder · October 2026",
    program: "AI Product Builder Program",
    intake: "1 Oct–27 Nov 2026",
    status: "active",
    learners: 34,
    capacity: 40,
    progress: 68,
    attendance: 91,
    onTrack: 26,
    watch: 6,
    intervention: 2,
    reviewQueue: 11,
    nextSession: "Today · Accessible Product Decisions",
    completionForecast: 88,
  },
  {
    id: "prompt-sep-2026",
    title: "Prompt & Context Engineering · September 2026",
    program: "Prompt & Context Engineering",
    intake: "7 Sep–30 Oct 2026",
    status: "active",
    learners: 28,
    capacity: 32,
    progress: 84,
    attendance: 94,
    onTrack: 24,
    watch: 3,
    intervention: 1,
    reviewQueue: 6,
    nextSession: "Friday · Capstone preparation",
    completionForecast: 93,
  },
  {
    id: "leaders-jan-2027",
    title: "AI Leadership for Organisations · January 2027",
    program: "AI Leadership for Organisations",
    intake: "11 Jan–5 Feb 2027",
    status: "upcoming",
    learners: 21,
    capacity: 35,
    progress: 0,
    attendance: 0,
    onTrack: 21,
    watch: 0,
    intervention: 0,
    reviewQueue: 0,
    nextSession: "Orientation · 8 Jan 2027",
    completionForecast: 91,
  },
  {
    id: "builder-jul-2026",
    title: "AI Product Builder · July 2026",
    program: "AI Product Builder Program",
    intake: "6 Jul–28 Aug 2026",
    status: "completed",
    learners: 31,
    capacity: 36,
    progress: 100,
    attendance: 92,
    onTrack: 29,
    watch: 2,
    intervention: 0,
    reviewQueue: 0,
    nextSession: "Completed",
    completionForecast: 94,
  },
];

export const CORE_LEARNERS: LearnerRecord[] = [
  {
    id: "maya-chen",
    initials: "MC",
    name: "Maya Chen",
    email: "maya.chen@example.com",
    cohortId: "builder-oct-2026",
    cohort: "AI Product Builder · Oct 2026",
    program: "AI Product Builder Program",
    progress: 76,
    attendance: 97,
    average: 92,
    risk: "on-track",
    submissions: "9 / 9",
    project: "Knowledge Operations Copilot",
    credentialReadiness: 82,
    lastActive: "12 minutes ago",
    accommodations: "Captions and written instructions",
    languages: "English + Mandarin",
  },
  {
    id: "ronald-isabirye",
    initials: "RI",
    name: "Ronald Isabirye",
    email: "ronald@example.com",
    cohortId: "builder-oct-2026",
    cohort: "AI Product Builder · Oct 2026",
    program: "AI Product Builder Program",
    progress: 68,
    attendance: 94,
    average: 88,
    risk: "on-track",
    submissions: "8 / 9",
    project: "Kijani Market Checkout",
    credentialReadiness: 74,
    lastActive: "Now",
    accommodations: "None recorded",
    languages: "English + Luganda",
  },
  {
    id: "nakato-kezia",
    initials: "NK",
    name: "Nakato Kezia",
    email: "nakato@example.com",
    cohortId: "builder-oct-2026",
    cohort: "AI Product Builder · Oct 2026",
    program: "AI Product Builder Program",
    progress: 72,
    attendance: 91,
    average: 90,
    risk: "on-track",
    submissions: "9 / 9",
    project: "Kijani Market Checkout",
    credentialReadiness: 78,
    lastActive: "6 minutes ago",
    accommodations: "Keyboard-first workflow",
    languages: "English + Luganda",
  },
  {
    id: "jamal-thompson",
    initials: "JT",
    name: "Jamal Thompson",
    email: "jamal@example.com",
    cohortId: "builder-oct-2026",
    cohort: "AI Product Builder · Oct 2026",
    program: "AI Product Builder Program",
    progress: 43,
    attendance: 66,
    average: 61,
    risk: "intervention",
    submissions: "5 / 9",
    project: "Sales Call Insight Engine",
    credentialReadiness: 38,
    lastActive: "2 days ago",
    accommodations: "Private support note available",
    languages: "English",
  },
  {
    id: "sofia-martinez",
    initials: "SM",
    name: "Sofia Martinez",
    email: "sofia@example.com",
    cohortId: "builder-oct-2026",
    cohort: "AI Product Builder · Oct 2026",
    program: "AI Product Builder Program",
    progress: 71,
    attendance: 89,
    average: 87,
    risk: "watch",
    submissions: "8 / 9",
    project: "Campaign Performance Copilot",
    credentialReadiness: 70,
    lastActive: "3 hours ago",
    accommodations: "Extended reading time",
    languages: "Spanish + English",
  },
  {
    id: "ibrahim-yusuf",
    initials: "IY",
    name: "Ibrahim Yusuf",
    email: "ibrahim@example.com",
    cohortId: "prompt-sep-2026",
    cohort: "Prompt & Context · Sep 2026",
    program: "Prompt & Context Engineering",
    progress: 89,
    attendance: 96,
    average: 96,
    risk: "on-track",
    submissions: "12 / 12",
    project: "Policy Research Context Pack",
    credentialReadiness: 94,
    lastActive: "44 minutes ago",
    accommodations: "None recorded",
    languages: "Arabic + English",
  },
  {
    id: "grace-nwosu",
    initials: "GN",
    name: "Grace Nwosu",
    email: "grace@example.com",
    cohortId: "prompt-sep-2026",
    cohort: "Prompt & Context · Sep 2026",
    program: "Prompt & Context Engineering",
    progress: 92,
    attendance: 98,
    average: 91,
    risk: "on-track",
    submissions: "12 / 12",
    project: "Consulting Knowledge Copilot",
    credentialReadiness: 96,
    lastActive: "1 hour ago",
    accommodations: "None recorded",
    languages: "English + Igbo",
  },
  {
    id: "hugo-mendes",
    initials: "HM",
    name: "Hugo Mendes",
    email: "hugo@example.com",
    cohortId: "prompt-sep-2026",
    cohort: "Prompt & Context · Sep 2026",
    program: "Prompt & Context Engineering",
    progress: 78,
    attendance: 86,
    average: 79,
    risk: "watch",
    submissions: "11 / 12",
    project: "Operations Prompt Library",
    credentialReadiness: 72,
    lastActive: "Yesterday",
    accommodations: "Low-bandwidth recordings",
    languages: "Portuguese + English",
  },
];

export const GENERATED_LEARNERS: LearnerRecord[] = Array.from({ length: 56 }, (_, index) => {
  const firstNames = [
    "Abena",
    "Achieng",
    "Aisha",
    "Akello",
    "Amara",
    "Anita",
    "Ayanda",
    "Brian",
    "Chinwe",
    "Dayo",
    "Doreen",
    "Elias",
    "Fatima",
    "Hamza",
    "Imani",
    "Janet",
    "Joseph",
    "Joy",
    "Kato",
    "Lilian",
    "Mariam",
    "Moses",
    "Naledi",
    "Nana",
    "Noah",
    "Okello",
    "Patience",
    "Peter",
    "Ruth",
    "Samuel",
    "Tendo",
    "Yara",
    "Zuri",
  ];
  const lastNames = [
    "Adebayo",
    "Akurut",
    "Banda",
    "Bwire",
    "Diallo",
    "Kagwa",
    "Kamau",
    "Kato",
    "Kintu",
    "Mensah",
    "Moyo",
    "Mugisha",
    "Mwangi",
    "Nabirye",
    "Ndlovu",
    "Nsubuga",
    "Okafor",
    "Omondi",
    "Sarr",
    "Ssemanda",
    "Tadesse",
    "Tumusiime",
  ];
  const first = firstNames[index % firstNames.length];
  const last = lastNames[Math.floor(index / firstNames.length) % lastNames.length];
  const cohort = index % 4 === 0 ? COHORTS[1] : COHORTS[0];
  const risk: LearnerRecord["risk"] =
    index % 17 === 0 ? "intervention" : index % 7 === 0 ? "watch" : "on-track";
  const progress =
    risk === "intervention" ? 42 + (index % 8) : risk === "watch" ? 58 + (index % 12) : 67 + (index % 25);
  return {
    id: `learner-${index + 1}`,
    initials: `${first[0]}${last[0]}`,
    name: `${first} ${last}`,
    email: `${first}.${last}`.toLowerCase() + "@example.com",
    cohortId: cohort.id,
    cohort: cohort.title
      .replace("AI Product Builder · ", "Builder · ")
      .replace("Prompt & Context Engineering · ", "Prompt · "),
    program: cohort.program,
    progress,
    attendance: Math.max(61, 84 + (index % 15) - (risk === "intervention" ? 20 : 0)),
    average: Math.max(55, 76 + (index % 20) - (risk === "intervention" ? 18 : 0)),
    risk,
    submissions: `${6 + (index % 4)} / 9`,
    project: [
      "Customer Support Copilot",
      "Inventory Forecast Assistant",
      "School Operations Agent",
      "Community Health Navigator",
    ][index % 4],
    credentialReadiness: Math.max(30, progress - 3),
    lastActive: ["Now", "18 minutes ago", "2 hours ago", "Yesterday"][index % 4],
    accommodations: index % 9 === 0 ? "Captions and transcript" : "None recorded",
    languages: ["English", "English + Luganda", "French + English", "Arabic + English"][index % 4],
  };
});

export const LEARNERS = [...CORE_LEARNERS, ...GENERATED_LEARNERS];

export const LESSONS: LessonRecord[] = [
  {
    id: "problem-framing",
    title: "Problem Framing Before AI",
    module: "Module 1 · Foundations",
    program: "AI Product Builder Program",
    format: "Workshop",
    status: "Published",
    duration: "75 min",
    accessibility: 100,
    engagement: 94,
    version: "v2.3",
  },
  {
    id: "prompt-contracts",
    title: "Prompt Contracts and Structured Outputs",
    module: "Module 3 · Reliable AI",
    program: "AI Product Builder Program",
    format: "Self-paced",
    status: "Published",
    duration: "52 min",
    accessibility: 96,
    engagement: 91,
    version: "v2.1",
  },
  {
    id: "context-architecture",
    title: "Context Architecture and Retrieval Boundaries",
    module: "Module 4 · Context Systems",
    program: "AI Product Builder Program",
    format: "Clinic",
    status: "Published",
    duration: "68 min",
    accessibility: 100,
    engagement: 96,
    version: "v1.8",
  },
  {
    id: "api-boundaries",
    title: "APIs, Data Boundaries and Server Actions",
    module: "Module 5 · Systems",
    program: "AI Product Builder Program",
    format: "Live",
    status: "Review",
    duration: "85 min",
    accessibility: 82,
    engagement: 0,
    version: "v1.4",
  },
  {
    id: "accessible-decisions",
    title: "Accessible Product Decisions",
    module: "Module 6 · Trustworthy Interfaces",
    program: "AI Product Builder Program",
    format: "Live",
    status: "Published",
    duration: "75 min",
    accessibility: 100,
    engagement: 0,
    version: "v3.0",
  },
  {
    id: "evidence-claims",
    title: "From Claims to Evidence",
    module: "Module 7 · Evaluation",
    program: "AI Product Builder Program",
    format: "Workshop",
    status: "Draft",
    duration: "60 min",
    accessibility: 88,
    engagement: 0,
    version: "v0.9",
  },
  {
    id: "responsible-use",
    title: "Responsible Use and Human Authority",
    module: "Module 8 · Proof",
    program: "AI Product Builder Program",
    format: "Self-paced",
    status: "Draft",
    duration: "47 min",
    accessibility: 92,
    engagement: 0,
    version: "v1.0",
  },
];

export const ASSIGNMENTS: AssignmentRecord[] = [
  {
    id: "checkout-sprint",
    title: "Build an Accessible, Trustworthy Checkout",
    program: "AI Product Builder Program",
    module: "Module 6",
    cohorts: ["Builder Oct 2026"],
    status: "Published",
    due: "28 Nov 2026",
    submissions: 27,
    graded: 16,
    revisions: 3,
    rubric: "Product Evidence Rubric v3",
    weight: 20,
  },
  {
    id: "context-pack",
    title: "Context Pack with Retrieval Failure Evidence",
    program: "AI Product Builder Program",
    module: "Module 4",
    cohorts: ["Builder Oct 2026"],
    status: "Published",
    due: "18 Nov 2026",
    submissions: 34,
    graded: 29,
    revisions: 4,
    rubric: "Context Systems Rubric v2",
    weight: 15,
  },
  {
    id: "problem-brief",
    title: "Problem Framing and Decision Boundary",
    program: "AI Product Builder Program",
    module: "Module 1",
    cohorts: ["Builder Oct 2026"],
    status: "Closed",
    due: "15 Oct 2026",
    submissions: 34,
    graded: 34,
    revisions: 2,
    rubric: "Problem Framing Rubric v4",
    weight: 10,
  },
  {
    id: "prompt-library",
    title: "Prompt Library with Evaluation Set",
    program: "Prompt & Context Engineering",
    module: "Module 5",
    cohorts: ["Prompt Sep 2026"],
    status: "Published",
    due: "12 Nov 2026",
    submissions: 26,
    graded: 20,
    revisions: 2,
    rubric: "Prompt Quality Rubric v2",
    weight: 25,
  },
  {
    id: "leadership-brief",
    title: "AI Opportunity and Governance Brief",
    program: "AI Leadership for Organisations",
    module: "Module 2",
    cohorts: ["Leaders Jan 2027"],
    status: "Draft",
    due: "30 Jan 2027",
    submissions: 0,
    graded: 0,
    revisions: 0,
    rubric: "Leadership Decision Rubric v1",
    weight: 30,
  },
];

export const SUBMISSIONS: SubmissionRecord[] = [
  {
    id: "submission-maya-context",
    learnerId: "maya-chen",
    learner: "Maya Chen",
    initials: "MC",
    assignment: "Context Pack with Retrieval Failure Evidence",
    cohort: "Builder Oct 2026",
    submitted: "Today · 8:24 AM",
    age: "3h",
    status: "Pending",
    risk: "low",
    evidence: 4,
    aiDisclosure: true,
    integritySignal: "Clear process and source trail",
  },
  {
    id: "submission-ronald-checkout",
    learnerId: "ronald-isabirye",
    learner: "Ronald Isabirye",
    initials: "RI",
    assignment: "Accessible Checkout Sprint",
    cohort: "Builder Oct 2026",
    submitted: "Today · 7:52 AM",
    age: "4h",
    status: "In review",
    risk: "low",
    evidence: 8,
    aiDisclosure: true,
    integritySignal: "AI activity and human decisions visible",
  },
  {
    id: "submission-jamal-context",
    learnerId: "jamal-thompson",
    learner: "Jamal Thompson",
    initials: "JT",
    assignment: "Context Pack",
    cohort: "Builder Oct 2026",
    submitted: "Yesterday · 5:11 PM",
    age: "19h",
    status: "Revision",
    risk: "high",
    evidence: 2,
    score: 54,
    aiDisclosure: false,
    integritySignal: "Consent and retention evidence missing",
  },
  {
    id: "submission-sofia-interface",
    learnerId: "sofia-martinez",
    learner: "Sofia Martinez",
    initials: "SM",
    assignment: "Accessible Checkout Sprint",
    cohort: "Builder Oct 2026",
    submitted: "Yesterday · 1:20 PM",
    age: "23h",
    status: "Pending",
    risk: "medium",
    evidence: 6,
    aiDisclosure: true,
    integritySignal: "Recommendation boundary clearly documented",
  },
  {
    id: "submission-ibrahim-prompts",
    learnerId: "ibrahim-yusuf",
    learner: "Ibrahim Yusuf",
    initials: "IY",
    assignment: "Prompt Library with Evaluation Set",
    cohort: "Prompt Sep 2026",
    submitted: "Monday · 4:18 PM",
    age: "2d",
    status: "Moderation",
    risk: "low",
    evidence: 11,
    score: 96,
    aiDisclosure: true,
    integritySignal: "Second-marker calibration required",
  },
  {
    id: "submission-grace-capstone",
    learnerId: "grace-nwosu",
    learner: "Grace Nwosu",
    initials: "GN",
    assignment: "Capstone Evidence Pack",
    cohort: "Prompt Sep 2026",
    submitted: "Monday · 2:06 PM",
    age: "2d",
    status: "Pending",
    risk: "low",
    evidence: 14,
    aiDisclosure: true,
    integritySignal: "Complete evidence trail",
  },
  {
    id: "submission-hugo-library",
    learnerId: "hugo-mendes",
    learner: "Hugo Mendes",
    initials: "HM",
    assignment: "Prompt Library with Evaluation Set",
    cohort: "Prompt Sep 2026",
    submitted: "Sunday · 9:44 PM",
    age: "3d",
    status: "Revision",
    risk: "medium",
    evidence: 7,
    score: 75,
    aiDisclosure: true,
    integritySignal: "Human review evidence incomplete",
  },
  {
    id: "submission-nakato-checkout",
    learnerId: "nakato-kezia",
    learner: "Nakato Kezia",
    initials: "NK",
    assignment: "Accessible Checkout Sprint",
    cohort: "Builder Oct 2026",
    submitted: "Today · 9:58 AM",
    age: "2h",
    status: "Pending",
    risk: "low",
    evidence: 9,
    aiDisclosure: true,
    integritySignal: "Implementation and evaluation trace linked",
  },
];

export const LABS: LabRecord[] = [
  {
    id: "ai-app-builder",
    slug: "ai-app-builder",
    title: "AI-App Builder Lab",
    category: "Build",
    icon: "terminal",
    entitlement: "AIPB-APP",
    access: "Included",
    assignedCohorts: 2,
    activeRuns: 19,
    completion: 71,
    description: "Prototype and test full-stack AI applications inside isolated, evidence-aware workspaces.",
  },
  {
    id: "agentic-ai",
    slug: "agentic-ai",
    title: "Agentic AI Lab",
    category: "Agents",
    icon: "bot",
    entitlement: "AIPB-AGENT",
    access: "Included",
    assignedCohorts: 1,
    activeRuns: 8,
    completion: 58,
    description: "Design bounded agents, tools, approvals, memory, safety and handoff evaluation.",
  },
  {
    id: "data-predictive-ai",
    slug: "data-predictive-ai",
    title: "Data & Predictive AI Lab",
    category: "Data",
    icon: "database",
    entitlement: "AIPB-DATA",
    access: "Included",
    assignedCohorts: 1,
    activeRuns: 6,
    completion: 44,
    description: "Explore clean datasets, train baseline models and compare explainable predictions.",
  },
  {
    id: "conversational-ai",
    slug: "conversational-ai",
    title: "Conversational AI Lab",
    category: "Conversation",
    icon: "message",
    entitlement: "AIPB-CONV",
    access: "Included",
    assignedCohorts: 2,
    activeRuns: 12,
    completion: 65,
    description: "Create multilingual conversational experiences with clear escalation and evaluation.",
  },
  {
    id: "science-simulation",
    slug: "science-simulation",
    title: "AI Science Simulation Lab",
    category: "Science",
    icon: "atom",
    entitlement: "AIPB-SCIENCE",
    access: "Included",
    assignedCohorts: 0,
    activeRuns: 3,
    completion: 22,
    description: "Model physical systems, compare hypotheses and inspect 2D or 3D live simulations.",
  },
  {
    id: "graphics-3d-cad-ai",
    slug: "graphics-3d-cad-ai",
    title: "Graphics, 3D & CAD AI Lab",
    category: "Design",
    icon: "cube",
    entitlement: "AIPB-DESIGN",
    access: "Included",
    assignedCohorts: 1,
    activeRuns: 4,
    completion: 49,
    description: "Generate, refine and inspect 2D and 3D design concepts with evidence of each iteration.",
  },
  {
    id: "deep-learning",
    slug: "deep-learning",
    title: "Deep Learning Lab",
    category: "Models",
    icon: "brain",
    entitlement: "ADV-DEEP-LEARNING",
    access: "Advanced",
    assignedCohorts: 0,
    activeRuns: 0,
    completion: 0,
    description:
      "Run guided neural-network experiments with visual training metrics and safe compute limits.",
  },
  {
    id: "space-systems-mission-ai",
    slug: "space-systems-mission-ai",
    title: "Space Systems & Mission AI Lab",
    category: "Space",
    icon: "rocket",
    entitlement: "ADV-SPACE",
    access: "Advanced",
    assignedCohorts: 0,
    activeRuns: 0,
    completion: 0,
    description: "Plan mission constraints, visualise orbital scenarios and evaluate AI-assisted decisions.",
  },
];

export const LAB_INSTRUCTOR_SECTIONS: LabInstructorWorkspaceSection[] = [
  {
    id: "dashboard",
    label: "Teaching Overview",
    icon: "home",
    detail: "Active learners, grading priorities, Lab questions and cohort completion.",
    badge: "Live",
  },
  {
    id: "one-to-one",
    label: "1:1 Session Requests",
    icon: "calendar",
    detail: "Review learner goals, accept a request or offer an appropriate alternative.",
    badge: "5",
  },
  {
    id: "content",
    label: "Programs, Labs & Notes",
    icon: "book",
    detail: "Manage practical activities, approved lesson notes, translations and release rules.",
  },
  {
    id: "authoring",
    label: "Exercise Authoring",
    icon: "edit",
    detail: "Create practical tasks, workspace type, limits, rubric, release and assessment rules.",
  },
  {
    id: "grading",
    label: "Grading Queue",
    icon: "clipboard",
    detail: "Review practical evidence with the rubric and give useful human feedback.",
    badge: "12",
  },
  {
    id: "learners",
    label: "Learners & Cohorts",
    icon: "users",
    detail: "See enrolment, progress, Lab access, support signals and cohort completion.",
  },
  {
    id: "feedback",
    label: "Feedback Library",
    icon: "message",
    detail: "Reuse constructive feedback while keeping each response specific to the learner.",
  },
  {
    id: "reports",
    label: "Learning Reports",
    icon: "chart",
    detail: "Inspect completion, skill evidence, usage and teaching-support needs.",
  },
  {
    id: "help",
    label: "Instructor Help",
    icon: "help",
    detail: "Open role-specific Lab guidance, policies and support without leaving the Lab platform.",
  },
];

export const PROJECT_REVIEWS: ProjectReview[] = [
  {
    id: "project-ronald",
    learner: "Ronald Isabirye",
    initials: "RI",
    title: "Kijani Market Checkout",
    type: "Team project",
    milestone: "Accessible checkout and evidence",
    status: "In review",
    architecture: 86,
    evidence: 82,
    verification: 79,
    risk: "low",
  },
  {
    id: "project-maya",
    learner: "Maya Chen",
    initials: "MC",
    title: "Knowledge Operations Copilot",
    type: "Client simulation",
    milestone: "Context and source review",
    status: "Pending",
    architecture: 92,
    evidence: 90,
    verification: 88,
    risk: "low",
  },
  {
    id: "project-jamal",
    learner: "Jamal Thompson",
    initials: "JT",
    title: "Sales Call Insight Engine",
    type: "Practice project",
    milestone: "Problem and data review",
    status: "Revision",
    architecture: 66,
    evidence: 54,
    verification: 48,
    risk: "high",
  },
  {
    id: "project-sofia",
    learner: "Sofia Martinez",
    initials: "SM",
    title: "Campaign Performance Copilot",
    type: "Client simulation",
    milestone: "Interface and recommendation review",
    status: "Verified",
    architecture: 88,
    evidence: 93,
    verification: 91,
    risk: "low",
  },
  {
    id: "project-nakato",
    learner: "Nakato Kezia",
    initials: "NK",
    title: "Inclusive Commerce Components",
    type: "Practice project",
    milestone: "Accessibility verification",
    status: "Pending",
    architecture: 84,
    evidence: 87,
    verification: 83,
    risk: "medium",
  },
];

export const MESSAGE_THREADS: MessageThread[] = [
  {
    id: "thread-maya",
    subject: "Clarification on source retention",
    participant: "Maya Chen",
    initials: "MC",
    role: "Learner",
    channel: "learner",
    unread: 1,
    preview: "Could you clarify whether project sources should be archived…",
    messages: [
      {
        id: "m1",
        author: "Maya Chen",
        initials: "MC",
        text: "Could you clarify whether project-specific sources should be archived or deleted after the engagement ends?",
        time: "10:35 AM",
      },
      {
        id: "m2",
        author: "You",
        initials: "AM",
        text: "Treat retention as a business-policy decision. Record the owner, duration, deletion trigger and any legal hold in the context pack.",
        time: "10:41 AM",
        mine: true,
      },
    ],
  },
  {
    id: "thread-builder-cohort",
    subject: "Builder cohort · week 6 announcement",
    participant: "Builder October 2026",
    initials: "BO",
    role: "Cohort",
    channel: "cohort",
    unread: 0,
    preview: "This week we move from interface claims into evidence…",
    messages: [
      {
        id: "m3",
        author: "You",
        initials: "AM",
        text: "This week we move from interface claims into evidence. Submit one unresolved risk before Friday’s clinic.",
        time: "7:12 AM",
        mine: true,
      },
    ],
  },
  {
    id: "thread-academic-ops",
    subject: "Capstone panel calibration",
    participant: "Academic Operations",
    initials: "AO",
    role: "Staff",
    channel: "staff",
    unread: 2,
    preview: "Please complete rubric calibration before the panel…",
    messages: [
      {
        id: "m4",
        author: "Academic Operations",
        initials: "AO",
        text: "Please complete the capstone rubric calibration before the November 15 panel. Two criteria changed in version 2.5.",
        time: "8:45 AM",
      },
      {
        id: "m5",
        author: "Theo Morgan",
        initials: "TM",
        text: "I added examples for responsible-use evidence. Please review the Exceptional descriptor.",
        time: "9:02 AM",
      },
    ],
  },
  {
    id: "thread-jamal",
    subject: "Private support follow-up",
    participant: "Jamal Thompson",
    initials: "JT",
    role: "Learner",
    channel: "learner",
    unread: 0,
    preview: "Thank you for checking in. I can attend Wednesday…",
    messages: [
      {
        id: "m6",
        author: "You",
        initials: "AM",
        text: "I noticed your attendance and submission pace changed. Which support option would be most useful this week?",
        time: "Yesterday",
        mine: true,
      },
      {
        id: "m7",
        author: "Jamal Thompson",
        initials: "JT",
        text: "Thank you for checking in. I can attend Wednesday office hours and will bring the consent questions.",
        time: "Yesterday",
      },
    ],
  },
];

export const NOTIFICATIONS: NotificationRecord[] = [
  {
    id: "n1",
    icon: "clipboard",
    title: "Context pack ready for review",
    detail: "Maya Chen submitted Context Pack v2 with four evidence artifacts.",
    time: "8 minutes ago",
    category: "review",
    unread: true,
    area: "assessment",
    page: "submission-maya-context",
  },
  {
    id: "n2",
    icon: "alert",
    title: "Learner intervention recommended",
    detail: "Jamal Thompson is below attendance and assignment-pace thresholds.",
    time: "37 minutes ago",
    category: "learner",
    unread: true,
    area: "cohorts",
    page: "learner-jamal-thompson",
  },
  {
    id: "n3",
    icon: "video",
    title: "Nine questions are waiting",
    detail: "Accessible Product Decisions starts today at 3:00 PM EAT.",
    time: "1 hour ago",
    category: "class",
    unread: true,
    area: "class",
    page: "session-accessible-product-decisions-2026-11-04",
  },
  {
    id: "n4",
    icon: "users",
    title: "Attendance corrections requested",
    detail: "Two learners asked for attendance review after the last workshop.",
    time: "Yesterday",
    category: "cohort",
    unread: false,
    area: "class",
    page: "attendance",
  },
  {
    id: "n5",
    icon: "book",
    title: "Accessibility review blocking lesson",
    detail: "APIs, Data Boundaries and Server Actions still needs captions and code descriptions.",
    time: "Yesterday",
    category: "system",
    unread: false,
    area: "teaching",
    page: "lesson-api-boundaries",
  },
  {
    id: "n6",
    icon: "award",
    title: "Capstone calibration due",
    detail: "Complete the panel rubric calibration before 15 November.",
    time: "2 days ago",
    category: "review",
    unread: false,
    area: "projects",
    page: "capstone-grace",
  },
];

export const SEARCH_RECORDS: SearchRecord[] = [
  {
    id: "s1",
    area: "cohorts",
    page: "learner-maya-chen",
    icon: "users",
    title: "Maya Chen",
    detail: "Builder October 2026 · 76% progress · 92 average",
    label: "Learner",
  },
  {
    id: "s2",
    area: "class",
    page: "session-accessible-product-decisions-2026-11-04",
    icon: "video",
    title: "Accessible Product Decisions",
    detail: "Today at 3:00 PM EAT · code 4821-771-204",
    label: "Class",
  },
  {
    id: "s3",
    area: "buildRooms",
    page: "group-build-checkout",
    icon: "message",
    title: "Kijani checkout Build Room",
    detail: "124 connected · one conditional checkpoint",
    label: "Build Room",
  },
  {
    id: "s4",
    area: "labs",
    page: "lab-ai-app-builder",
    icon: "flask",
    title: "AI-App Builder Lab",
    detail: "19 active learner runs · Builder October 2026",
    label: "Lab",
  },
  {
    id: "s4a",
    area: "labs",
    page: "workspace",
    icon: "external",
    title: "Instructor Lab workspace",
    detail: "Independent labs.chatoner.com role · create, guide and grade",
    label: "Connected platform",
  },
  {
    id: "s4b",
    area: "buildRooms",
    page: "small-groups",
    icon: "grid",
    title: "Kijani Build Room small groups",
    detail: "6 persistent teams · 34 learners · separate branches and huddles",
    label: "Build Room groups",
  },
  {
    id: "s5",
    area: "assessment",
    page: "submission-ronald-checkout",
    icon: "clipboard",
    title: "Ronald Isabirye · checkout submission",
    detail: "8 evidence items · AI disclosure present",
    label: "Submission",
  },
  {
    id: "s6",
    area: "projects",
    page: "project-ronald",
    icon: "folder",
    title: "Kijani Market Checkout",
    detail: "Project review · architecture 86 · evidence 82",
    label: "Project",
  },
  {
    id: "s7",
    area: "teaching",
    page: "lesson-api-boundaries",
    icon: "book",
    title: "APIs, Data Boundaries and Server Actions",
    detail: "Lesson v1.4 · accessibility review pending",
    label: "Lesson",
  },
  {
    id: "s8",
    area: "projects",
    page: "capstone-grace",
    icon: "award",
    title: "Grace Nwosu capstone",
    detail: "Panel review · evidence complete · defense 15 Nov",
    label: "Capstone",
  },
  {
    id: "s9",
    area: "engagement",
    page: "messages",
    icon: "mail",
    title: "Academic Operations",
    detail: "Capstone panel calibration · 2 unread",
    label: "Message",
  },
];

export const CORE_BUILD_ROOM_MEMBERS: MemberDirectoryItem[] = [
  {
    id: "amina",
    initials: "AM",
    name: "Amina Mutesi",
    role: "Instructor",
    detail: "Lead facilitator · final human reviewer",
    group: "Academy staff",
    online: true,
  },
  {
    id: "daniel",
    initials: "DM",
    name: "Daniel Mwesigwa",
    role: "Mentor",
    detail: "Product and accessibility",
    group: "Academy staff",
    online: true,
  },
  {
    id: "ronald",
    initials: "RI",
    name: "Ronald Isabirye",
    role: "Learner",
    detail: "Product lead",
    group: "Core team",
    online: true,
  },
  {
    id: "nakato",
    initials: "NK",
    name: "Nakato Kezia",
    role: "Learner",
    detail: "Frontend implementation",
    group: "Core team",
    online: true,
  },
  {
    id: "joel",
    initials: "JM",
    name: "Joel Mugisha",
    role: "Learner",
    detail: "Evaluation and QA",
    group: "Core team",
    online: true,
  },
  {
    id: "asha",
    initials: "AS",
    name: "Asha Namusoke",
    role: "Learner",
    detail: "Research and data",
    group: "Core team",
    online: false,
  },
  {
    id: "maya",
    initials: "MC",
    name: "Maya Chen",
    role: "Learner",
    detail: "Context systems and evidence lead",
    group: "Cohort",
    online: true,
  },
  {
    id: "jamal",
    initials: "JT",
    name: "Jamal Thompson",
    role: "Learner",
    detail: "Consent and responsible-use practice",
    group: "Cohort",
    online: false,
  },
  {
    id: "grace",
    initials: "GN",
    name: "Grace Nwosu",
    role: "Learner",
    detail: "Capstone candidate and peer reviewer",
    group: "Cohort",
    online: true,
  },
  {
    id: "sofia",
    initials: "SM",
    name: "Sofia Martinez",
    role: "Learner",
    detail: "Accessible interface evaluation",
    group: "Cohort",
    online: true,
  },
  {
    id: "ibrahim",
    initials: "IY",
    name: "Ibrahim Yusuf",
    role: "Learner",
    detail: "Prompt evaluation and calibration",
    group: "Cohort",
    online: false,
  },
  {
    id: "ai-builder",
    initials: "AI",
    name: "AI Builder",
    role: "AI agent",
    detail: "Permission-scoped implementation agent",
    group: "Approved AI",
    online: true,
  },
];

export const MEMBER_FIRST_NAMES = [
  "Abena",
  "Achieng",
  "Aisha",
  "Akello",
  "Amara",
  "Anita",
  "Ayanda",
  "Brian",
  "Chinwe",
  "Dayo",
  "Doreen",
  "Elias",
  "Fatima",
  "Grace",
  "Hamza",
  "Ibrahim",
  "Imani",
  "Janet",
  "Joseph",
  "Joy",
  "Kato",
  "Lilian",
  "Mariam",
  "Moses",
  "Naledi",
  "Nana",
  "Noah",
  "Okello",
  "Patience",
  "Peter",
  "Ruth",
  "Samuel",
  "Sofia",
  "Tendo",
  "Yara",
  "Zuri",
];

export const MEMBER_LAST_NAMES = [
  "Adebayo",
  "Akurut",
  "Banda",
  "Bwire",
  "Diallo",
  "Kagwa",
  "Kamau",
  "Kato",
  "Kintu",
  "Mensah",
  "Moyo",
  "Mugisha",
  "Mwangi",
  "Nabirye",
  "Ndlovu",
  "Nsubuga",
  "Okafor",
  "Omondi",
  "Sarr",
  "Ssemanda",
  "Tadesse",
  "Tumusiime",
];

export const EXTENDED_BUILD_ROOM_MEMBERS: MemberDirectoryItem[] = Array.from({ length: 112 }, (_, index) => {
  const first = MEMBER_FIRST_NAMES[index % MEMBER_FIRST_NAMES.length];
  const last = MEMBER_LAST_NAMES[Math.floor(index / MEMBER_FIRST_NAMES.length) % MEMBER_LAST_NAMES.length];
  const role: MemberDirectoryItem["role"] =
    index < 3
      ? "Instructor"
      : index < 10
        ? "Mentor"
        : index < 14
          ? "Reviewer"
          : index < 18
            ? "Observer"
            : "Learner";
  const group: MemberDirectoryItem["group"] =
    role === "Learner" || role === "Observer" ? "Cohort" : "Academy staff";
  return {
    id: `member-${index + 1}`,
    initials: `${first[0]}${last[0]}`,
    name: `${first} ${last}`,
    role,
    detail:
      role === "Learner"
        ? `October 2026 cohort · learner ${String(index + 1).padStart(3, "0")}`
        : `${role} access`,
    group,
    online: index % 3 !== 0,
  };
});

export const BUILD_ROOM_MEMBERS = [...CORE_BUILD_ROOM_MEMBERS, ...EXTENDED_BUILD_ROOM_MEMBERS];

export const INITIAL_BUILD_ROOM_SUBGROUPS: BuildRoomSubgroup[] = [
  {
    id: "accessibility-experience",
    name: "Accessibility & Experience",
    channel: "subgroup-accessibility-experience",
    purpose: "Keyboard, screen-reader, validation and recoverable checkout experience.",
    parentRoomId: "group-build-checkout",
    cohortId: "builder-oct-2026",
    classSessionId: "accessible-product-decisions-2026-11-04",
    memberIds: ["ronald", "nakato", "sofia", "member-19", "member-20", "member-21"],
    leadId: "ronald",
    status: "Active",
    visibility: "Members + instructors",
    formation: "Balanced",
    language: "English + Luganda",
    activeNow: 4,
    progress: 78,
    evidence: 82,
    reviewCount: 1,
    branch: "team/accessibility-experience",
    labId: "ai-app-builder",
    huddleCode: "5412-880-116",
    due: "13 Nov 2026",
  },
  {
    id: "trust-safety",
    name: "Trust, Safety & Consent",
    channel: "subgroup-trust-safety",
    purpose: "Consent, privacy, human authority, safe data and exception decisions.",
    parentRoomId: "group-build-checkout",
    cohortId: "builder-oct-2026",
    classSessionId: "accessible-product-decisions-2026-11-04",
    memberIds: ["jamal", "ibrahim", "member-22", "member-23", "member-24"],
    leadId: "jamal",
    status: "Needs review",
    visibility: "Members + instructors",
    formation: "Project role",
    language: "English + Arabic",
    activeNow: 2,
    progress: 64,
    evidence: 59,
    reviewCount: 2,
    branch: "team/trust-safety",
    labId: "governance-safety",
    huddleCode: "6124-337-904",
    due: "13 Nov 2026",
  },
  {
    id: "api-data",
    name: "API & Data Boundaries",
    channel: "subgroup-api-data",
    purpose: "Server actions, secrets, data contracts, fixtures and integration boundaries.",
    parentRoomId: "group-build-checkout",
    cohortId: "builder-oct-2026",
    classSessionId: "accessible-product-decisions-2026-11-04",
    memberIds: ["asha", "member-25", "member-26", "member-27", "member-28", "member-29"],
    leadId: "asha",
    status: "Active",
    visibility: "Members + instructors",
    formation: "Balanced",
    language: "English + Swahili",
    activeNow: 3,
    progress: 72,
    evidence: 70,
    reviewCount: 0,
    branch: "team/api-data",
    labId: "data-predictive-ai",
    huddleCode: "7318-452-118",
    due: "13 Nov 2026",
  },
  {
    id: "evaluation-evidence",
    name: "Evaluation & Evidence",
    channel: "subgroup-evaluation-evidence",
    purpose: "Automated checks, human observation, failure evidence and traceability.",
    parentRoomId: "group-build-checkout",
    cohortId: "builder-oct-2026",
    classSessionId: "evaluation-clinic-2026-11-06",
    memberIds: ["joel", "maya", "member-30", "member-31", "member-32", "member-33"],
    leadId: "joel",
    status: "Active",
    visibility: "Members + instructors",
    formation: "Project role",
    language: "English + Mandarin",
    activeNow: 5,
    progress: 86,
    evidence: 91,
    reviewCount: 1,
    branch: "team/evaluation-evidence",
    labId: "ai-app-builder",
    huddleCode: "8401-225-673",
    due: "13 Nov 2026",
  },
  {
    id: "automation-agent",
    name: "Automation & Agent Boundaries",
    channel: "subgroup-automation-agent",
    purpose: "Bounded agents, workflow states, approvals, retries and stop controls.",
    parentRoomId: "group-build-checkout",
    cohortId: "builder-oct-2026",
    classSessionId: "evaluation-clinic-2026-11-06",
    memberIds: ["grace", "member-34", "member-35", "member-36", "member-37", "member-38"],
    leadId: "grace",
    status: "Active",
    visibility: "Members + instructors",
    formation: "Balanced",
    language: "English + French",
    activeNow: 3,
    progress: 69,
    evidence: 66,
    reviewCount: 0,
    branch: "team/automation-agent",
    labId: "agentic-ai",
    huddleCode: "2904-651-342",
    due: "13 Nov 2026",
  },
  {
    id: "demo-story",
    name: "Demo, Story & Handoff",
    channel: "subgroup-demo-story",
    purpose: "Integrated demonstration, explanation, multilingual handoff and audience questions.",
    parentRoomId: "group-build-checkout",
    cohortId: "builder-oct-2026",
    classSessionId: "kijani-project-review-2026-11-10",
    memberIds: ["member-39", "member-40", "member-41", "member-42", "member-43"],
    leadId: "member-39",
    status: "Paused",
    visibility: "Members + instructors",
    formation: "Availability",
    language: "English + Luganda",
    activeNow: 1,
    progress: 48,
    evidence: 41,
    reviewCount: 0,
    branch: "team/demo-story",
    labId: "product-design",
    huddleCode: "4550-119-806",
    due: "13 Nov 2026",
  },
];

export function buildRoomMember(memberId: string) {
  return BUILD_ROOM_MEMBERS.find(item => item.id === memberId);
}

export function subgroupHuddleSession(group: BuildRoomSubgroup): ClassSession {
  return {
    id: `build-room-group-${group.id}`,
    title: `${group.name} Huddle`,
    code: group.huddleCode,
    dateLabel: "Today",
    timeLabel: "Starts when the instructor enters",
    localTime: "Your local time",
    duration: "35 minutes",
    instructor: "Amina Mutesi",
    program: "AI Product Builder Program",
    cohort: `${group.name} · small group`,
    mode: "Build Room small-group huddle",
    status: "live",
    preparation: ["Open the group plan", "Review the current blocker", "Confirm named owners and evidence"],
    agenda: ["Progress", "Blocker", "Decision", "Next action"],
    learnerCount: group.memberIds.length,
    questions: group.reviewCount,
    recording: "off",
  };
}

export function languageCode(language: string) {
  const codes: Record<string, string> = {
    English: "EN",
    Luganda: "LG",
    French: "FR",
    Arabic: "AR",
    Swahili: "SW",
    Mandarin: "ZH",
    Spanish: "ES",
    Portuguese: "PT",
    Hindi: "HI",
    German: "DE",
    Italian: "IT",
    Japanese: "JA",
    Korean: "KO",
    Yoruba: "YO",
    Igbo: "IG",
    Zulu: "ZU",
    None: "",
  };
  return codes[language] || language.slice(0, 2).toUpperCase();
}

export function instructorClassHandoffUrl(
  session: ClassSession,
  language: LanguageSettings,
  mode: "host" | "rehearsal" = "host",
) {
  const params = new URLSearchParams({
    code: session.code,
    role: "instructor",
    mode,
    instructor: "amina-mutesi",
    program: session.program,
    cohort: session.cohort,
    session: session.id,
    language: session.language || language.primary,
    secondary_language: language.secondary,
    captions: "on",
    source: "academy-instructor",
    return_to: `${ACADEMY_RETURN}/class/${session.id}`,
  });
  return `${CLASS_HOST_BASE}?${params.toString()}`;
}

export function learnerClassInviteUrl(session: ClassSession) {
  const params = new URLSearchParams({
    code: session.code,
    source: "academy-instructor",
    session: session.id,
    return_to: "https://instructors.academy.chatoner.com/#/instructor/class",
  });
  return `${CLASS_JOIN_BASE}?${params.toString()}`;
}

export function instructorLabsWorkspaceUrl(
  section: LabInstructorSectionId,
  language: LanguageSettings,
  lab?: LabRecord,
  cohortId = "builder-oct-2026",
  assignment = "checkout-sprint",
  mode: "instructor" | "learner-preview" = "instructor",
) {
  const params = new URLSearchParams({
    role: mode === "learner-preview" ? "learner" : "instructor",
    view: section,
    mode,
    instructor: "amina-mutesi",
    cohort: cohortId,
    assignment,
    language: language.primary,
    secondary_language: language.secondary,
    source: "academy-instructor",
    handoff: "academy-sso",
    return_to: `${ACADEMY_RETURN}/labs/${lab?.id || section}`,
  });
  if (lab) {
    params.set("lab", lab.slug);
    params.set("entitlement", lab.entitlement);
  }
  return `${LABS_BASE}/?${params.toString()}`;
}

export function instructorLabHandoffUrl(
  lab: LabRecord,
  cohortId = "builder-oct-2026",
  assignment = "checkout-sprint",
  mode: "facilitator" | "learner-preview" = "facilitator",
  section: LabInstructorSectionId = "dashboard",
  language: LanguageSettings = {
    primary: "English",
    secondary: "Luganda",
    showTranslation: true,
    retainOriginal: true,
  },
) {
  return instructorLabsWorkspaceUrl(
    section,
    language,
    lab,
    cohortId,
    assignment,
    mode === "learner-preview" ? "learner-preview" : "instructor",
  );
}

export const openExternalHandoff = openExternalUrl;

export function createClassCode() {
  const group = () => String(Math.floor(100 + Math.random() * 900));
  return `${Math.floor(1000 + Math.random() * 9000)}-${group()}-${group()}`;
}
